import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  notFound,
  Outlet,
  redirect,
  Scripts,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { useEffect } from "react";
import { SiteFooter, SiteHeader } from "@/components/site";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { ogLocaleAlternates } from "@/lib/seo";
import { classifyStaleUrl } from "@/lib/stale-url";
import { getLocale } from "@/paraglide/runtime";

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=JetBrains+Mono:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=optional";

import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import appCss from "../styles.css?url";

interface MyRouterContext {
  queryClient: QueryClient;
}

// SSR theme class storage. Set in beforeLoad, read in RootDocument.
// Safe: beforeLoad and RootDocument render synchronously within the same request.
let __ssrThemeClass = "";

export const Route = createRootRouteWithContext<MyRouterContext>()({
  beforeLoad: async ({ location }) => {
    if (typeof document !== "undefined") {
      // Client-side
      document.documentElement.setAttribute("lang", getLocale());
      return;
    }

    // Server-side: clean up stale/malformed URLs a past link-generation bug
    // leaked to Google (route templates, `{}`, doubled paths). Recoverable
    // doubled paths (`/x/x`) 301 to the real page — this recovers link equity;
    // pure garbage that reaches here 404s cleanly. See src/lib/stale-url.ts.
    const stale = classifyStaleUrl(location.pathname);
    if (stale?.type === "redirect") {
      throw redirect({ href: stale.to, statusCode: 301 });
    }
    if (stale?.type === "gone") {
      throw notFound();
    }

    // Dark as the SSR default. The blocking THEME_SCRIPT swaps to the user's
    // preference before first paint, so no flash occurs.
    __ssrThemeClass = "dark-theme dark font-theme";
  },

  // `matches` lets the root head see a global not-found: without this the 404
  // page inherits the homepage <title>, which crawlers read as a duplicate
  // (soft-404 signal). No route-level head runs for an unmatched URL.
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Disable iOS Safari Data Detectors. Otherwise Safari auto-links phone
      // numbers, dates, and lap-time-like strings into <a> tags BEFORE React
      // hydrates, mutating the server HTML → hydration mismatch (React #418).
      // This is iOS-Safari-only (Chrome/WebKit don't do it), which is why it
      // never reproduced in Chrome/CI and only shows on real iPhones.
      {
        name: "format-detection",
        content: "telephone=no, date=no, address=no, email=no",
      },
      {
        title: matches.some((m) => m.globalNotFound)
          ? "404 — Page not found | Toolverse"
          : "Toolverse — Your Command Center for Specialized Tools",
      },

      // SEO Meta Tags
      {
        name: "description",
        content:
          "Toolverse is your command center for specialized web tools. Each tool is purpose-built to solve a specific problem exceptionally well.",
      },
      { name: "robots", content: "index, follow" },

      // Open Graph Tags
      { property: "og:type", content: "website" },
      {
        property: "og:title",
        content: "Toolverse — Your Command Center for Specialized Tools",
      },
      {
        property: "og:description",
        content:
          "Your command center for specialized web tools. Built for precision, speed, and reliability.",
      },
      { property: "og:site_name", content: "Toolverse" },
      // og:locale is emitted per-route via i18nOgMeta ("@/lib/seo") so it
      // reflects the actual locale; og:locale:alternate ×9 is rendered as JSX
      // in RootDocument (head-manager dedupe). Do NOT hardcode either here.
      {
        property: "og:image",
        content: "https://toolverse.me/og-image.svg",
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content:
          "Toolverse — Precision web tools for every specialized problem",
      },

      // Twitter Card Tags. Only the card type lives here: twitter:title /
      // twitter:description / twitter:image are deliberately NOT set site-wide.
      // Twitter falls back to og:title / og:description / og:image, which every
      // route sets per page — hardcoding them here made every tool page share
      // the homepage's card text and image.
      { name: "twitter:card", content: "summary_large_image" },

      // Theme Color
      { name: "theme-color", content: "#0f1729" },

      // Google AdSense site verification. This meta tag is Google's official,
      // zero-JS method to confirm site ownership for AdSense review — it must be
      // discoverable in the server-rendered <head> (the lazy-loaded ad script in
      // AdPlacement is NOT visible to the AdSense verification crawler). The
      // publisher id is public (also in /ads.txt), so it is safe to inline.
      {
        name: "google-adsense-account",
        content: "ca-pub-5529994201153878",
      },
    ],
    links: [
      // Google Fonts preconnect — warms the connection for the non-blocking font loader script
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      // Keep preconnects minimal (browsers effectively use ~4). Fonts above +
      // the two most critical third parties below. Remaining ad/analytics
      // origins (doubleclick, adtrafficquality, google-analytics) are reached
      // via these and don't each need their own preconnect.
      {
        rel: "preconnect",
        href: "https://pagead2.googlesyndication.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "preconnect",
        href: "https://www.googletagmanager.com",
        crossOrigin: "anonymous",
      },
      // Google Fonts — preload hint only; actual stylesheet loads async via
      // FONT_SCRIPT. crossOrigin MUST match the stylesheet link created by
      // FONT_SCRIPT (anonymous) or the browser discards the preload and
      // re-fetches ("request credentials mode does not match" warning).
      {
        rel: "preload",
        as: "style",
        href: GOOGLE_FONTS_URL,
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.json" },
      // hreflang + canonical are emitted PER-ROUTE via i18nLinks ("@/lib/seo"),
      // so they reflect the actual path and cover all 10 locales. Do NOT add a
      // site-wide hreflang here (it would point every page at the homepage).
      { rel: "icon", type: "image/svg+xml", href: "/toolverse-icon.svg" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://toolverse.me/#website",
              name: "Toolverse",
              url: "https://toolverse.me/",
              description:
                "Your command center for specialized web tools. Each tool is purpose-built for a specific domain, crafted with obsessive attention to detail.",
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate:
                    "https://toolverse.me/f1-fantasy/statistics?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "Organization",
              "@id": "https://toolverse.me/#organization",
              name: "Toolverse",
              url: "https://toolverse.me/",
              logo: {
                "@type": "ImageObject",
                url: "https://toolverse.me/toolverse-icon.svg",
              },
              sameAs: [],
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://toolverse.me/#app",
              name: "Toolverse",
              url: "https://toolverse.me/",
              applicationCategory: "UtilitiesApplication",
              operatingSystem: "Web",
              description:
                "A curated collection of precision-built web tools, each solving a specific problem exceptionally well. Currently featuring F1 Fantasy analytics tools.",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootDocument,
  component: RootLayout,
});

// Blocking script: applies the theme before first paint.
// localStorage override ('toolverse-theme') wins; otherwise follows the OS
// (prefers-color-scheme), including live OS changes mid-session. Also keeps
// the theme-color meta (mobile browser chrome) in sync. MutationObserver
// prevents hydration from wiping classes — ThemeToggle's direct class writes
// keep font-theme, so it stays inert.
const THEME_SCRIPT = `(function(){try{var d=document.documentElement;function pref(){try{var s=localStorage.getItem('toolverse-theme');if(s==='light'||s==='dark')return s}catch(e){}return matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'}function apply(){var t=pref();d.classList.remove('light-theme','dark-theme','dark');d.classList.add(t==='dark'?'dark-theme':'light-theme','font-theme');if(t==='dark')d.classList.add('dark');var m=document.querySelector('meta[name="theme-color"]');if(m)m.setAttribute('content',t==='dark'?'#0f1729':'#f7f8fb')}apply();matchMedia('(prefers-color-scheme: dark)').addEventListener('change',apply);new MutationObserver(function(m){m.forEach(function(r){if(r.attributeName==='class'&&!d.classList.contains('font-theme'))apply()})}).observe(d,{attributes:true,attributeFilter:['class']})}catch(e){}})()`;

// Non-blocking Google Fonts loader — avoids render-blocking stylesheet.
// display=optional: fonts are used only if ready within the first render window;
// no layout shift occurs since the fallback font is used otherwise.
const FONT_SCRIPT = `(function(){var l=document.createElement('link');l.rel='stylesheet';l.crossOrigin='anonymous';l.href='https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=JetBrains+Mono:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=optional';l.media='print';l.onload=function(){this.onload=null;this.media='all'};document.head.appendChild(l)})()`;

// Google Analytics 4 — standard async tag (visible in HTML source for GA verification).
// send_page_view: false prevents duplicate page_view on initial load;
// useGoogleAnalytics hook fires page_view per SPA navigation.
// Skipped in dev mode and when VITE_GA_MEASUREMENT_ID is not set.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const GA_INIT_SCRIPT =
  GA_MEASUREMENT_ID && !import.meta.env.DEV
    ? `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}',{send_page_view:false});`
    : null;

// Google AdSense loader — async script in <head> so the AdSense verification
// crawler can find the active ad code (mirrors the GA tag above; the lazy
// IntersectionObserver loader in AdPlacement is invisible to that crawler).
// Gated on the publisher-id env var + production so dev/CI without the var are
// unaffected. AdPlacement dedupes against this tag (no double injection).
const ADSENSE_CLIENT_ID = import.meta.env.VITE_ADSENSE_PUBLISHER_ID;

function RootDocument({ children }: { children: React.ReactNode }) {
  // Server: use SSR-computed theme class, then reset for next request.
  // Client: preserve whatever the blocking script set on <html>,
  // so hydration failure doesn't wipe theme classes.
  let themeClass: string | undefined;
  if (typeof document === "undefined") {
    themeClass = __ssrThemeClass || undefined;
    __ssrThemeClass = "";
  } else {
    themeClass = document.documentElement.className || undefined;
  }

  return (
    <html
      lang={getLocale()}
      className={themeClass || undefined}
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
        {/* og:locale:alternate ×9 as plain JSX: TanStack's head manager dedupes
            meta by `property`, so emitting these via route head() kept only
            one. React renders them verbatim here. */}
        {ogLocaleAlternates().map((code) => (
          <meta key={code} property="og:locale:alternate" content={code} />
        ))}
        {/* THEME_SCRIPT, FONT_SCRIPT, and GA scripts are trusted static string literals — no user content */}
        <script dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />
        <script dangerouslySetInnerHTML={{ __html: FONT_SCRIPT }} />
        {GA_INIT_SCRIPT && (
          <>
            {/* Standard async gtag.js — must be a real script src tag for Google's verification crawler */}
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            />
            <script dangerouslySetInnerHTML={{ __html: GA_INIT_SCRIPT }} />
          </>
        )}
        {ADSENSE_CLIENT_ID && !import.meta.env.DEV && (
          /* Standard async AdSense loader — real script src tag for Google's verification crawler */
          <script
            async
            src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`}
            crossOrigin="anonymous"
          />
        )}
      </head>
      <body className="min-h-full flex flex-col">
        {children}
        {import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS !== "false" && (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
              TanStackQueryDevtools,
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}

function ScrollToTop() {
  const pathname = useLocation({ select: (l) => l.pathname });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RootLayout() {
  useGoogleAnalytics();
  return (
    <>
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1 flex flex-col">
        <Outlet />
      </main>
      <SiteFooter />
    </>
  );
}
